import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { useUserAndGameContext } from "../../../../contexts/UserAndGameContext";
import { useTiersGameContext } from "../../context/TiersGameContext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getRankSuffix } from "../../../../utils/GetRankSuffix";
import { DateTime } from "luxon";

const GolfTiersDisplay = () => {
    const {
        interval,
        myLeaderboardInfo,
        pickIndex,
        allUsersEntries,
    } = useUserAndGameContext();

    const {
        intervalsInfo,
        players,
    } = useTiersGameContext();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const thisIntervalsDeadline = intervalsInfo?.deadline;
    const nowInNewYork = DateTime.now().setZone('America/New_York');
    const thisIntervalsDeadlineInNewYork = thisIntervalsDeadline ? DateTime.fromISO(thisIntervalsDeadline, { zone: 'America/New_York' }) : null;
    const hasCurrentIntervalStarted = thisIntervalsDeadlineInNewYork ? thisIntervalsDeadlineInNewYork < nowInNewYork : false;

    const [selectedEntry, setSelectedEntry] = useState(null);
    const [selectedLeaderboardInfo, setSelectedLeaderboardInfo] = useState(null);

    useEffect(() => {
        if (allUsersEntries && pickIndex !== null) {
            const entry = allUsersEntries[pickIndex];
            setSelectedEntry(entry);

            if (myLeaderboardInfo) {
                const selectedUsername = entry.name;
                const leaderboardInfo = myLeaderboardInfo?.myInfo?.find(user => user.username === selectedUsername);
                setSelectedLeaderboardInfo(leaderboardInfo);
            }
        }
    }, [allUsersEntries, pickIndex, myLeaderboardInfo]);

    const selectionsInfo = useMemo(() => {
        if (intervalsInfo) {
            return intervalsInfo.selections;
        }
        return null;
    }, [intervalsInfo]);
    
    const thisIntervalsRank = useMemo(() => {
        if (selectedLeaderboardInfo) {
            const ranks = selectedLeaderboardInfo?.intervalData?.find(rank => rank.interval === interval)?.rank;
            return ranks || null;
        }
        return null;
    }, [selectedLeaderboardInfo, interval]);

    const thisIntervalsScore = useMemo(() => {
        const score = selectedLeaderboardInfo?.intervalData?.find(rank => rank.interval === interval)?.intervalScore;
    
        if (score === 0) return '(E)';
        if (score > 0) return `(+${score})`;
        if (score < 0) return `(${score})`;
    
        return null;
    }, [selectedLeaderboardInfo, interval]);

    // Determine the two worst tiers to color red
    const worstTiers = useMemo(() => {
        // Only calculate worst tiers if the interval has started
        if (!hasCurrentIntervalStarted || !selectedEntry?.pool?.picks) return [];
        
        // Get all picks for the current interval
        const picksForInterval = selectedEntry.pool.picks.filter(pick => pick.interval === interval);
        
        // Convert to array for sorting
        const picksArray = picksForInterval.map(pick => ({
            tier: pick.tier,
            overallScore: pick.overallScore // Use the existing score directly
        }));
        
        // Filter out picks that don't have scores
        const picksWithScores = picksArray.filter(pick => pick.overallScore !== null && pick.overallScore !== undefined);
        
        // Sort by score (highest/worst first)
        // If tied, sort by tier (descending, so higher tiers come first)
        const sortedPicks = picksWithScores.sort((a, b) => {
            if (a.overallScore !== b.overallScore) {
                return b.overallScore - a.overallScore; // Sort by score descending (worst first)
            }
            return b.tier - a.tier; // If tied, sort by tier descending (higher first)
        });
        
        // Take the first two (worst) picks
        const twoWorstPicks = sortedPicks.slice(0, 2);
        
        // Return the tiers of the two worst picks
        return twoWorstPicks.map(pick => pick.tier);
    }, [selectedEntry, interval, hasCurrentIntervalStarted]);

    // Updated function with coloring logic
    function gatherPlayerInfoByInterval(interval, tier) {
        if (selectedEntry) {
            const playerInfo = selectedEntry?.pool?.picks?.find((pick) => pick.interval === interval && pick.tier === tier);
            if (playerInfo) {
                const playerId = playerInfo?.id;
                const player = players?.find((player) => player.PlayerID === Number(playerId));
                
                // Format the score
                let scoreText = '';
                let statusText = '';
                
                // Determine status
                if (playerInfo?.cut) {
                    statusText = 'Cut';
                } else if (playerInfo?.withdrawn) {
                    statusText = 'WD';
                }
                
                // Determine score
                if (playerInfo?.overallScore !== undefined && playerInfo?.overallScore !== null) {
                    if (playerInfo?.overallScore === 0) {
                        scoreText = '(E';
                    } else if (playerInfo?.overallScore > 0) {
                        scoreText = `(+${playerInfo?.overallScore}`;
                    } else {
                        scoreText = `(${playerInfo?.overallScore}`;
                    }
                    
                    // Add status if exists and close parenthesis
                    if (statusText) {
                        scoreText += ` ${statusText})`;
                    } else {
                        scoreText += ')';
                    }
                } else if (statusText) {
                    // If no score but has status
                    scoreText = `(${statusText})`;
                }
                
                // Check if this tier is one of the two worst
                const isWorstTier = worstTiers.includes(tier);
                
                return {
                    name: player ? `${player?.FirstName} ${player?.LastName}` : 'No Pick',
                    score: scoreText || null,
                    color: isWorstTier ? '#CC293C' : '#002129' // Red for worst tiers, default color otherwise
                };
            }
            return null;
        }
        return null;
    }

    // Updated renderTierTable function with color support
    const renderTierTable = () => {
        if (!selectionsInfo) return null;
        
        const totalTiers = selectionsInfo.length;
        const firstColumnCount = Math.ceil(totalTiers / 2);
        
        const firstColumn = selectionsInfo.slice(0, firstColumnCount);
        const secondColumn = selectionsInfo.slice(firstColumnCount);
        
        return (
            <Grid 
                container
                sx={{
                    width: '100%',
                    maxWidth: isMobile ? '400px' : '650px',
                    display: 'flex',
                    margin: '0 auto',
                    paddingLeft: isMobile ? '0px' : '50px',
                }}
            >
                <Grid item xs={6}>
                    {firstColumn?.map((tier) => {
                        const playerData = gatherPlayerInfoByInterval(interval, tier.tier);
                        return (
                            <Box
                                key={`tier-${tier.tier}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: isMobile ? '10px' : '16px',
                                        color: playerData?.color || '#002129',
                                        fontWeight: '700',
                                        textAlign: 'left',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {!hasCurrentIntervalStarted && (
                                        <Box component="span">Tier {tier.tier}: </Box>
                                    )}
                                    {playerData ? (
                                        <>
                                            <Box 
                                                component="span" 
                                                sx={{ 
                                                    display: 'inline-block',
                                                    maxWidth: isMobile ? playerData?.score ? '100px' : '140px' : '240px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    verticalAlign: 'bottom'
                                                }}
                                            >
                                                {playerData.name}
                                            </Box>
                                            {playerData.score && (
                                                <Box component="span"> {playerData.score}</Box>
                                            )}
                                        </>
                                    ) : (
                                        <Box component="span">TBD</Box>
                                    )}
                                </Typography>
                            </Box>
                        );
                    })}
                </Grid>
                <Grid item xs={6}>
                    {secondColumn?.map((tier) => {
                        const playerData = gatherPlayerInfoByInterval(interval, tier.tier);
                        return (
                            <Box
                                key={`tier-${tier.tier}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: isMobile ? '10px' : '16px',
                                        color: playerData?.color || '#002129',
                                        fontWeight: '700',
                                        textAlign: 'left',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {!hasCurrentIntervalStarted && (
                                        <Box component="span">Tier {tier.tier}: </Box>
                                    )}
                                    {playerData ? (
                                        <>
                                            <Box 
                                                component="span" 
                                                sx={{ 
                                                    display: 'inline-block',
                                                    maxWidth: isMobile ? playerData?.score ? '100px' : '140px' : '240px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    verticalAlign: 'bottom'
                                                }}
                                            >
                                                {playerData.name}
                                            </Box>
                                            {playerData.score && (
                                                <Box component="span"> {playerData.score}</Box>
                                            )}
                                        </>
                                    ) : (
                                        <Box component="span">TBD</Box>
                                    )}
                                </Typography>
                            </Box>
                        );
                    })}
                </Grid>
            </Grid>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                padding: '16px',
            }}
        >
            <Typography
                sx={{
                    fontSize: isMobile ? '12px' : '16px',
                    fontWeight: '700',
                    color: '#002129',
                    marginBottom: '8px',
                }}
            >
               {thisIntervalsRank ? `${getRankSuffix(thisIntervalsRank)}: ` : null} {selectedEntry?.name} {thisIntervalsScore ? ` ${thisIntervalsScore}` : null}
            </Typography>
            
            {renderTierTable()}
        </Box>
    );
};

export default GolfTiersDisplay;